import { useState, useRef, useEffect } from 'react';
import { api } from '@/store';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Sparkles, User, Bot, Zap, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';

export default function NexuPage() {
  const [messages, setMessages] = useState([
    { role: 'model', text: 'Olá, Senhor. Sou o NEXU. Posso renovar clientes, cobrar vencidos, gerar relatórios e mais. Como posso ajudar?' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ui, setUi] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);       // resposta falada
  const [interim, setInterim] = useState('');          // transcrição em tempo real
  const [speaking, setSpeaking] = useState(false);
  const recogRef = useRef(null);
  const activeListenRef = useRef(false);  // true enquanto o startListen está rodando (pausa o wake)
  const micGrantedRef = useRef(false);   // permissão de microfone já concedida nesta sessão
  const endRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', text: msg }];
    setMessages(newMsgs);
    setLoading(true);
    setUi(null);
    try {
      const history = messages.slice(1).map((m) => ({ role: m.role, text: m.text }));
      const r = await api.post('/nexu/chat', { message: msg, history });
      if (r.data.needs_key) {
        setMessages((m) => [...m, { role: 'model', text: r.data.text, needsKey: true }]);
      } else {
        setMessages((m) => [...m, { role: 'model', text: r.data.text }]);
        if (r.data.ui) setUi(r.data.ui);
        speak(r.data.text);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'model', text: 'Tive um problema ao processar, Senhor. Tente novamente.' }]);
    } finally { setLoading(false); }
  };

  // ---------- Síntese de fala (NEXU falando) ----------
  const audioRef = useRef(null);

  // converte PCM (L16) do Gemini em WAV tocável
  const pcmToWav = (b64, sampleRate) => {
    const bin = atob(b64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    const buffer = new ArrayBuffer(44 + len);
    const view = new DataView(buffer);
    const w = (off, str) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); };
    w(0, 'RIFF'); view.setUint32(4, 36 + len, true); w(8, 'WAVE'); w(12, 'fmt ');
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true); w(36, 'data'); view.setUint32(40, len, true);
    new Uint8Array(buffer, 44).set(bytes);
    return new Blob([buffer], { type: 'audio/wav' });
  };

  // fala com a voz NEURAL do Gemini (humana); se falhar, usa a voz do navegador
  const speakNeural = async (text) => {
    try {
      const r = await api.post('/nexu/tts', { text });
      const mime = r.data.mime || '';
      const rate = parseInt((mime.match(/rate=(\d+)/) || [])[1] || '24000');
      const blob = pcmToWav(r.data.audio, rate);
      const url = URL.createObjectURL(blob);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setSpeaking(true);
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
      return true;
    } catch (e) {
      console.warn('[NEXU] TTS neural indisponível, usando voz do navegador');
      return false;
    }
  };

  // escolhe a voz PT-BR mais natural disponível no dispositivo
  const pickBestVoice = () => {
    const vozes = window.speechSynthesis.getVoices().filter(v => v.lang && v.lang.toLowerCase().startsWith('pt'));
    if (!vozes.length) return null;
    // ordem de preferência: vozes neurais/naturais são muito melhores
    const prefer = [
      /natural/i,                                  // Microsoft Natural (excelente)
      /google/i,                                   // Google (boa)
      /(thalita|francisca|antonio|brenda|daniel|maria|luciana)/i, // vozes nomeadas neurais
      /online/i,
    ];
    for (const rx of prefer) {
      const v = vozes.find(v => rx.test(v.name));
      if (v) return v;
    }
    // prioriza pt-BR sobre pt-PT
    return vozes.find(v => v.lang.toLowerCase() === 'pt-br') || vozes[0];
  };

  const speak = async (text) => {
    if (!voiceOn || !text) return;
    // tenta a voz neural do Gemini primeiro (bem mais humana)
    const ok = await speakNeural(text);
    if (ok) return;
    if (!window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      // limpa markdown e quebra em frases para uma fala mais natural (com pausas)
      const clean = text.replace(/[*#_`>]/g, '').replace(/\n+/g, '. ').trim();
      const u = new SpeechSynthesisUtterance(clean);
      u.lang = 'pt-BR';
      u.rate = 1.0;      // ritmo natural
      u.pitch = 1.0;     // tom natural
      u.volume = 1.0;
      const best = pickBestVoice();
      if (best) u.voice = best;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    } catch (e) {}
  };
  const stopSpeak = () => {
    try { window.speechSynthesis.cancel(); } catch (e) {}
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch (e) {}
    setSpeaking(false);
  };

  // ---------- Reconhecimento de voz (usuário falando) ----------
  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startListen = async () => {
    if (!SR) { alert('Seu navegador não suporta reconhecimento de voz. Use o Chrome no computador.'); return; }
    if (activeListenRef.current) { console.log('[NEXU] já está ouvindo, ignoro'); return; }
    // encerra qualquer reconhecimento anterior que tenha ficado preso
    try { recogRef.current && recogRef.current.abort(); } catch (e) {}
    // garante permissão de microfone — só pede na PRIMEIRA vez (depois o navegador lembra)
    if (!micGrantedRef.current) {
      let jaConcedido = false;
      try {
        if (navigator.permissions) {
          const st = await navigator.permissions.query({ name: 'microphone' });
          if (st.state === 'granted') { jaConcedido = true; micGrantedRef.current = true; }
        }
      } catch (e) {}
      if (!jaConcedido) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());  // libera o mic logo após o teste
          micGrantedRef.current = true;
        } catch (err) {
          console.warn('[NEXU] microfone negado:', err);
          alert('Preciso de permissão para usar o microfone. Clique no cadeado da barra de endereço, permita o microfone, e escolha "Sempre permitir".');
          return;
        }
      }
    }
    stopSpeak();
    // pausa o wake word para não conflitar (causa do 'aborted')
    activeListenRef.current = true;
    await new Promise((res) => setTimeout(res, 250));  // dá tempo do wake soltar o microfone
    const r = new SR();
    r.lang = 'pt-BR'; r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    setInterim(''); setListening(true);
    let finalText = '';
    let got = false;
    r.onstart = () => console.log('[NEXU] escuta iniciada');
    r.onaudiostart = () => console.log('[NEXU] captando áudio');
    r.onresult = (e) => {
      got = true;
      let itr = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else itr += t;
      }
      setInterim(itr || finalText);
    };
    r.onerror = (e) => {
      console.warn('[NEXU] erro de voz:', e.error);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        alert('Microfone bloqueado. Permita o microfone nas configurações do navegador.');
      }
      setListening(false); setInterim('');
    };
    r.onend = () => {
      console.log('[NEXU] escuta encerrada. texto:', finalText);
      setListening(false);
      activeListenRef.current = false;
      const txt = (finalText || '').trim();
      setInterim('');
      if (txt) send(txt);
      // religa o wake word depois de um tempinho
    };
    recogRef.current = r;
    try { r.start(); } catch (err) { console.warn('[NEXU] falha ao iniciar:', err); setListening(false); }
  };
  const stopListen = () => { try { recogRef.current?.stop(); } catch (e) {} setListening(false); };

  // (Wake word 'Olá NEXU' agora é global — ver NexuVoiceOverlay)

  // carregar vozes + checar permissão de mic já concedida (evita pedir toda vez)
  useEffect(() => {
    if (window.speechSynthesis) window.speechSynthesis.getVoices();
    (async () => {
      try {
        if (navigator.permissions) {
          const st = await navigator.permissions.query({ name: 'microphone' });
          if (st.state === 'granted') micGrantedRef.current = true;
          st.onchange = () => { micGrantedRef.current = (st.state === 'granted'); };
        }
      } catch (e) {}
    })();
  }, []);

  const quick = ['Como está minha base?', 'Quais clientes estão vencidos?', 'Clientes vencendo essa semana'];

  return (
    <div className="max-w-2xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
      {/* Header futurista */}
      <div className="relative rounded-2xl overflow-hidden border border-cyan-500/25 bg-gradient-to-br from-cyan-500/[0.08] via-primary/[0.04] to-transparent p-4 mb-3">
        <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/20 blur-3xl animate-pulse" />
        <div className="flex items-center gap-3 relative">
          <div className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400 to-primary flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
            <span className="absolute inset-0 rounded-2xl border-2 border-cyan-400/40 animate-ping" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
              NEXU <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">AI</span>
            </h1>
            <p className="text-xs text-muted-foreground">{listening ? 'Ouvindo…' : speaking ? 'Falando…' : 'Assistente inteligente do seu painel'}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => { setVoiceOn(!voiceOn); stopSpeak(); }} title={voiceOn ? 'Voz ligada' : 'Voz desligada'}
              className={`w-9 h-9 rounded-xl flex items-center justify-center border transition-colors ${voiceOn ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400' : 'border-border/60 text-muted-foreground'}`}>
              {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto space-y-3 px-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-primary/15 text-primary' : 'bg-cyan-500/15 text-cyan-400'}`}>
              {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className={`rounded-2xl px-3.5 py-2.5 max-w-[80%] text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 border border-border/50'}`}>
              <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
              {m.needsKey && (
                <button onClick={() => navigate('/ai-assistant')} className="mt-2 text-xs font-semibold text-cyan-400 underline">
                  Configurar chave Gemini →
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Card de dados (UI futurista) */}
        {ui && <NexuUiCard ui={ui} onRenew={(cid) => send(`renovar o cliente ${cid}`)} />}

        {loading && (
          <div className="flex gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/15 text-cyan-400 flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>
            <div className="rounded-2xl px-4 py-3 bg-muted/50 border border-border/50">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        {interim && (
          <div className="flex gap-2.5 flex-row-reverse">
            <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>
            <div className="rounded-2xl px-3.5 py-2.5 bg-primary/10 border border-primary/20 text-sm italic text-muted-foreground">{interim}…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Sugestões rápidas */}
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 px-1 mb-2">
          {quick.map((q) => (
            <button key={q} onClick={() => send(q)}
              className="text-xs border border-border/60 rounded-full px-3 py-1.5 hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Orbe de voz (quando ouvindo/falando) */}
      {(listening || speaking) && (
        <div className="flex justify-center py-2">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <span className={`absolute inset-0 rounded-full ${listening ? 'bg-cyan-500/20' : 'bg-primary/20'} animate-ping`} />
            <span className={`absolute inset-2 rounded-full ${listening ? 'bg-cyan-500/30' : 'bg-primary/30'} animate-pulse`} />
            <div className={`relative w-10 h-10 rounded-full bg-gradient-to-br ${listening ? 'from-cyan-400 to-cyan-600' : 'from-primary to-cyan-500'} flex items-center justify-center`}>
              {listening ? <Mic className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 pt-2 border-t border-border/40">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={listening ? 'Ouvindo…' : 'Fale com o NEXU...'}
          className="flex-1 bg-muted/40 border border-border/60 rounded-xl px-4 py-3 text-sm focus:border-cyan-500/40 focus:outline-none" />
        <button onClick={() => (listening ? stopListen() : startListen())}
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${listening ? 'bg-red-500 text-white animate-pulse' : 'border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10'}`}
          title="Falar">
          {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button onClick={() => send()} disabled={loading || !input.trim()}
          className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-primary text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

function NexuUiCard({ ui, onRenew }) {
  const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
  const d = ui.data || {};
  if (ui.type === 'relatorio_base') {
    const cards = [['Total', d.total, 'text-foreground'], ['Ativos', d.ativos, 'text-green-500'], ['Vencendo', d.vencendo, 'text-amber-500'], ['Vencidos', d.vencidos, 'text-red-400']];
    return (
      <div className="ml-10 grid grid-cols-4 gap-2">
        {cards.map(([label, val, color]) => (
          <div key={label} className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] p-3 text-center">
            <p className={`text-xl font-bold ${color}`}>{val ?? 0}</p>
            <p className="text-[10px] text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'buscar_cliente') {
    return (
      <div className="ml-10 space-y-1.5">
        {(d.clientes || []).map((c) => (
          <div key={c.customer_id} className="rounded-xl border border-border/50 bg-muted/30 px-3 py-2 flex items-center justify-between">
            <div><p className="text-sm font-medium">{c.nome}</p><p className="text-[11px] text-muted-foreground">{c.usuario} · vence {c.vencimento || '—'}</p></div>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'planos_do_cliente') {
    return (
      <div className="ml-10 flex flex-wrap gap-2">
        {(d.planos || []).map((p) => (
          <div key={p.plan_id} className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] px-3 py-2 text-sm">
            <span className="font-medium">{p.nome}</span> · <span className="text-cyan-400">{brl(p.preco)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'listar_integracoes') {
    return (
      <div className="ml-10 flex flex-wrap gap-2">
        {(d.integracoes || []).map((i) => (
          <div key={i.integration_id} className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] px-3 py-2 text-sm">
            <span className="font-medium">{i.nome}</span> <span className="text-[10px] text-cyan-400">{i.tipo}</span>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'buscar_na_integracao') {
    return (
      <div className="ml-10 max-h-56 overflow-y-auto space-y-1.5 pr-1">
        {(d.encontrados || []).map((c, i) => (
          <div key={i} className="rounded-xl border border-border/50 bg-muted/30 px-3 py-2 flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{c.usuario} {c.ja_importado && <span className="text-[10px] text-amber-400">· já importado</span>}</p>
              <p className="text-[11px] text-muted-foreground truncate">{c.pacote || '—'} · vence {c.vencimento || '—'}{c.telefone ? ` · ${c.telefone}` : ''}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'planos_da_integracao') {
    return (
      <div className="ml-10 flex flex-wrap gap-2">
        {(d.planos || []).map((p) => (
          <div key={p.plan_id} className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] px-3 py-2 text-sm">
            <span className="font-medium">{p.nome}</span> · <span className="text-cyan-400">{brl(p.preco)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (ui.type === 'listar_vencidos' || ui.type === 'listar_vencendo') {
    const list = d.vencidos || d.vencendo || [];
    return (
      <div className="ml-10 max-h-52 overflow-y-auto space-y-1">
        {list.map((c, i) => (
          <div key={i} className="rounded-lg border border-border/50 px-3 py-1.5 text-sm flex justify-between">
            <span>{c.nome}</span><span className="text-xs text-muted-foreground">{c.venceu || c.vence}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}
